import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { dashboardService, type AdminAnalytics } from '../../../../services/dashboardService';
import type { AdminScope } from './useAdminScope';

const STALE_MS = 5 * 60 * 1000;

export interface AdminAnalyticsData {
  current: AdminAnalytics | null;
  previous: AdminAnalytics | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Fetches the scope window and its previous equivalent — every KPI delta,
 * compare-mode ghost line and insight rule diffs the two.
 */
export function useAdminAnalytics(scope: AdminScope): AdminAnalyticsData {
  const currentQuery = useQuery({
    queryKey: ['admin-analytics', scope.window.from, scope.window.to],
    queryFn: () => dashboardService.getAdminAnalytics(scope.window),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
  const previousQuery = useQuery({
    queryKey: ['admin-analytics', scope.prevWindow.from, scope.prevWindow.to],
    queryFn: () => dashboardService.getAdminAnalytics(scope.prevWindow),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
  return {
    current: currentQuery.data ?? null,
    previous: previousQuery.data ?? null,
    isLoading: currentQuery.isLoading,
    isFetching: currentQuery.isFetching || previousQuery.isFetching,
    isError: currentQuery.isError,
    error: currentQuery.error,
  };
}
